// src/utils/ColorUtils.js

export const calculateMeanColor = ({rCodes, gCodes, bCodes}) => {
    const meanR = Math.round(rCodes.reduce((a, b) => a + b, 0) / rCodes.length);
    const meanG = Math.round(gCodes.reduce((a, b) => a + b, 0) / gCodes.length);
    const meanB = Math.round(bCodes.reduce((a, b) => a + b, 0) / bCodes.length);
    return [meanR, meanG, meanB];
};

export const calculateMedianPerChannel = ({rCodes, gCodes, bCodes}) => {
    const median = (arr) => {
        const sorted = [...arr].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 !== 0 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
    };

    return [median(rCodes), median(gCodes), median(bCodes)];
};

export const calculateTrueMedianColor = ({rCodes, gCodes, bCodes}) => {
    // Combine into color objects
    const colors = rCodes.map((r, i) => ({r, g: gCodes[i], b: bCodes[i]}));

    // Calculate distances between all colors
    const distances = colors.map(c1 => ({
        color: c1,
        totalDistance: colors.reduce((sum, c2) => {
            return sum + Math.sqrt(
                Math.pow(c1.r - c2.r, 2) +
                Math.pow(c1.g - c2.g, 2) +
                Math.pow(c1.b - c2.b, 2)
            );
        }, 0)
    }));

    // Find color with minimum total distance
    const median = distances.reduce((min, curr) =>
        curr.totalDistance < min.totalDistance ? curr : min
    );

    return [median.color.r, median.color.g, median.color.b];
};

export const calculateStdDev = ({array, mean}) => {
    return Math.sqrt(array.reduce((sq, n) => sq + Math.pow(n - mean, 2), 0) / array.length);
};

/**
 * Converts an RGB color to HSL.
 * @param {number} r - Red (0-255)
 * @param {number} g - Green (0-255)
 * @param {number} b - Blue (0-255)
 * @returns {number[]} [h, s, l] - h in [0, 360), s/l in [0, 1]
 */
export const rgbToHsl = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [h * 360, s, l];
};

/**
 * Converts an HSL color to RGB.
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-1)
 * @param {number} l - Lightness (0-1)
 * @returns {number[]} [r, g, b] in range [0, 255]
 */
export const hslToRgb = (h, s, l) => {
  h /= 360;
  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
};

/**
 * Brightens a color by increasing its lightness in HSL space.
 * @param {number[]} rgb - Array of [r, g, b]
 * @param {number} factor - Factor to increase lightness by (0 to 1). e.g., 0.2 adds 20% lightness.
 * @param {Object} options - Optional settings
 * @param {boolean} options.preserveSaturation - Whether to keep saturation unchanged (default: false)
 * @returns {number[]} New [r, g, b] array.
 */
export const brightenColorHsl = (rgb, factor, options = {}) => {
  const [h, s, l] = rgbToHsl(...rgb);
  // Increase lightness, clamping at 1.0
  const newL = Math.min(1, l + factor);

  // Optional: reduce saturation as you brighten to avoid neon colors
  let newS = s;
  if (!options.preserveSaturation) {
    // Reduce saturation more aggressively for larger brightness increases
    newS = s * (1 - (factor * 0.3));
  }

  return hslToRgb(h, newS, newL);
};

/**
 * Darkens a color by decreasing its lightness in HSL space.
 * @param {number[]} rgb - Array of [r, g, b]
 * @param {number} factor - Factor to decrease lightness by (0 to 1). e.g., 0.2 reduces lightness by 20%.
 * @param {Object} options - Optional settings
 * @param {boolean} options.preserveSaturation - Whether to keep saturation unchanged (default: false)
 * @param {number} options.minLightness - Minimum lightness to prevent pure black (default: 0.05)
 * @returns {number[]} New [r, g, b] array.
 */
export const darkenColorHsl = (rgb, factor, options = {}) => {
  const { preserveSaturation = false, minLightness = 0.05 } = options;
  const [h, s, l] = rgbToHsl(...rgb);

  // Decrease lightness, clamping at minLightness to avoid pure black
  const newL = Math.max(minLightness, l - factor);

  // Optional: adjust saturation as you darken
  let newS = s;
  if (!preserveSaturation) {
    // Slightly increase saturation when darkening to maintain color vibrancy
    newS = Math.min(1, s * (1 + (factor * 0.2)));
  }

  return hslToRgb(h, newS, newL);
};

/**
 * Sets the absolute lightness of a color in HSL space.
 * @param {number[]} rgb - Array of [r, g, b]
 * @param {number} lightness - Target lightness (0 to 1)
 * @param {Object} options - Optional settings
 * @param {boolean} options.adjustSaturation - Whether to adjust saturation for better results (default: true)
 * @returns {number[]} New [r, g, b] array.
 */
export const setColorLightness = (rgb, lightness, options = {}) => {
  const { adjustSaturation = true } = options;
  const [h, s, l] = rgbToHsl(...rgb);

  const newL = Math.max(0.05, Math.min(0.95, lightness));
  let newS = s;

  if (adjustSaturation) {
    // When moving to extremes of lightness, reduce saturation to avoid neon colors
    const distanceFromMiddle = Math.abs(newL - 0.5);
    newS = s * (1 - distanceFromMiddle * 0.3);
  }

  return hslToRgb(h, newS, newL);
};

/**
 * Creates a color manager that tracks original colors and allows adjustments
 * @param {Array} originalColors - Original grid colors array
 * @returns {Object} Color manager with adjustment methods
 */
export const createColorManager = (originalColors) => {
  let currentColors = JSON.parse(JSON.stringify(originalColors)); // Deep copy
  let adjustmentHistory = [];
  let currentAdjustment = 0;

  return {
    getCurrentColors: () => currentColors,

    getOriginalColors: () => JSON.parse(JSON.stringify(originalColors)),

    brighten: (factor = 0.1) => {
      currentColors = currentColors.map(row =>
        row.map(cell => {
          if (!cell || typeof cell.r === 'undefined') return cell;
          const [r, g, b] = brightenColorHsl([cell.r, cell.g, cell.b], factor);
          return {
            ...cell,
            color: `rgb(${r}, ${g}, ${b})`,
            r, g, b
          };
        })
      );
      currentAdjustment += factor;
      adjustmentHistory.push({ type: 'brighten', factor });
      return currentColors;
    },

    darken: (factor = 0.1) => {
      currentColors = currentColors.map(row =>
        row.map(cell => {
          if (!cell || typeof cell.r === 'undefined') return cell;
          const [r, g, b] = darkenColorHsl([cell.r, cell.g, cell.b], factor);
          return {
            ...cell,
            color: `rgb(${r}, ${g}, ${b})`,
            r, g, b
          };
        })
      );
      currentAdjustment -= factor;
      adjustmentHistory.push({ type: 'darken', factor });
      return currentColors;
    },

    reset: () => {
      currentColors = JSON.parse(JSON.stringify(originalColors));
      adjustmentHistory = [];
      currentAdjustment = 0;
      return currentColors;
    },

    getCurrentAdjustment: () => currentAdjustment,

    getAdjustmentHistory: () => [...adjustmentHistory]
  };
};

export default {
    calculateMeanColor,
    calculateMedianPerChannel,
    calculateTrueMedianColor,
    calculateStdDev,
    hslToRgb,
    rgbToHsl,
    brightenColorHsl,
    darkenColorHsl,
    setColorLightness,
    createColorManager
};